import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialog as MatDialog,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import {
  NgxScannerQrcodeComponent,
  ScannerQRCodeResult,
  ScannerQRCodeConfig,
} from 'ngx-scanner-qrcode';
import { of, Subject } from 'rxjs';
import { delay, takeUntil, debounceTime } from 'rxjs/operators';

import { PicklistApiService } from '../../../shared/services/api/picklist-api.service';
import { trackByValue } from '../../../shared/util';
import {
  PicklistPacking,
  PicklistPackingItem,
  PicklistPackingKitItem,
} from '../../interfaces';
import { PicklistVirtualCalculatorFormComponent } from '../picklist-virtual-calculator-form/picklist-virtual-calculator-form.component';

import { EItemType } from './../../../order/interfaces/order.interface';
import { DialogConfig } from './../../../shared/configs/dialog.config';
import { UiConfirmationDialogComponent } from './../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';
import { UiWarningDialogComponent } from './../../../ui/components/ui-warning-dialog/ui-warning-dialog.component';

@Component({
  selector: 'lum-picklist-verify',
  templateUrl: './picklist-verify.component.html',
  styleUrls: ['./picklist-verify.component.scss'],
})
export class PicklistVerifyComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(NgxScannerQrcodeComponent, { static: false })
  scanner :NgxScannerQrcodeComponent;

  @ViewChild('searchInputRef')
  searchInputRef :ElementRef;

  currentPacking :PicklistPacking;
  loadingScan :boolean = false;

  autoPlay :boolean = false;
  shouldBeep :boolean = false;

  isCallbackTriggered :boolean = false;

  scannerConfig :ScannerQRCodeConfig = {
    fps: 1,
    vibrate: 300,
    constraints: { audio: false, video: true },
  };

  selectedCameraDevice :string | null = null;

  manualSearchSubject = new Subject<string>();
  barcodeScanSubject = new Subject<string>();
  qRScanSubject = new Subject<string>();

  trackByValue = trackByValue;

  scan :any = {
    quantity: 1,
    sku: null,
  };

  eItemType = EItemType;

  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data :any,
    private dialogRef :MatDialogRef<PicklistVerifyComponent>,
    private dialog :MatDialog,
    private picklistService :PicklistApiService,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnInit() :void {
    this.currentPacking = this.data.packing;
    this.getDataFromService();
    this.onAutoBarcodeScan();
    this.onAutoQRScan();
    this.onManualSearch();

    const storedDevice = localStorage.getItem('selected_camera_device');
    if (storedDevice) {
      this.selectedCameraDevice = storedDevice;

      const deviceIndex = this.scanner?.devices?.value.findIndex(
        (device) => device.deviceId === storedDevice,
      );
      this.scannerConfig.deviceActive = deviceIndex;
    }
  }

  ngAfterViewInit() {
    this.searchInputRef?.nativeElement?.focus();

    if (this.autoPlay) {
      this.openScanner();
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSubmit() :void {
    this.dialogRef.close(true);
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onSelectDevice($event :any) :void {
    this.selectedCameraDevice = $event;
    localStorage.setItem('selected_camera_device', $event);

    const deviceIndex = this.scanner?.devices?.value.findIndex(
      (device) => device.deviceId === this.selectedCameraDevice,
    );
    this.scannerConfig.deviceActive = deviceIndex;

    this.scanner.playDevice($event).toPromise();
    this.cd.detectChanges();
  }

  openScanner() :void {
    this.scanner.start().toPromise();
    this.cd.detectChanges();
    if (this.scannerConfig.deviceActive) {
      this.onSelectDevice(this.scannerConfig.deviceActive);
    }
  }

  onBarcodeScanChange(event :Event) :void {
    if (this.isCallbackTriggered) {
      return;
    }

    this.isCallbackTriggered = true;

    const inputElement = event.target as HTMLInputElement;

    /**
     * when dealing with barcode scanners that emulate keyboard input, and it has to do with how JavaScript events are processed and the speed at which the barcode scanner sends the characters.
     * Barcode scanners usually send input very fast, sometimes faster than the browser can process the keystrokes. In your case, it seems like the "Enter" key event is being processed before
     * the last character input event is, thus missing the last character in your Angular method. This is especially problematic because JavaScript events are asynchronous, and event handling timing may vary.
     */
    of(null)
      .pipe(delay(50)) // adjust delay as needed
      .subscribe(() => {
        const inputValue = inputElement.value;
        this.barcodeScanSubject.next(inputValue);
      });
  }

  onAutoBarcodeScan() :void {
    this.barcodeScanSubject
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((searchTerm :string) => {
        this.onScan();
      });
  }

  onManualSearchChange(event :Event) :void {
    if (this.isCallbackTriggered) {
      return;
    }

    this.isCallbackTriggered = true;

    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;
    this.manualSearchSubject.next(inputValue);
  }

  onManualSearch() :void {
    this.manualSearchSubject
      .pipe(debounceTime(650), takeUntil(this.onDestroy$))
      .subscribe((searchTerm :string) => {
        this.onScan();
      });
  }

  onQRScanChange(result :ScannerQRCodeResult[]) :void {
    if (this.loadingScan) {
      return;
    }

    this.shouldBeep = false;

    //If result.typeName = "ZBAR_EAN13" and the first character is a 0, remove the 0.  It is a UPC-A code.
    if (
      result[0] &&
      result[0].typeName === 'ZBAR_EAN13' &&
      result[0].value.charAt(0) === '0'
    ) {
      result[0].value = result[0].value.substring(1);
    }

    if (!result || result.length <= 0) {
      this.loadingScan = false;

      return;
    }

    if (result[0] && this.scan.sku === result[0].value) {
      this.loadingScan = false;

      return;
    }

    this.scan.sku = result[0] && result[0].value ? result[0].value : null;

    this.qRScanSubject.next(this.scan.sku);
  }

  onAutoQRScan() :void {
    this.qRScanSubject
      .pipe(debounceTime(500), takeUntil(this.onDestroy$))
      .subscribe((searchTerm :string) => {
        this.onScan();
      });
  }

  onVerifyAll() :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Are you sure you want to manual verify all order?',
          message: 'This will fulfill the scanned quantity',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.currentPacking?.items?.forEach(
            (item :PicklistPackingItem, index :number) => {
              item.scanQuantity = item.baseUomQuantity;

              if (item.type === this.eItemType.KIT) {
                item.kitItems.forEach(
                  (kitItem :PicklistPackingKitItem, index :number) => {
                    kitItem.scanQuantity =
                      kitItem.baseUomQuantity * item.baseUomQuantity;
                  },
                );
              }
            },
          );
        }
      });
  }

  onClearAll() :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title:
            'Are you sure you want to reset the verification process of all order?',
          message: 'This will reset the scanned quantity',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.currentPacking?.items?.forEach(
            (item :PicklistPackingItem, index :number) => {
              item.scanQuantity = 0;

              if (item.type === this.eItemType.KIT) {
                item.kitItems.forEach(
                  (kitItem :PicklistPackingKitItem, index :number) => {
                    kitItem.scanQuantity = 0;
                  },
                );
              }
            },
          );
        }
      });
  }

  onScan() :void {
    if (this.scan.sku) {
      this.picklistService
        .scanBarcode({
          orderId: this.currentPacking.id,
          params: {
            searchQuery: this.scan.sku,
          },
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :any) => {
            if (response.id) {
              const items = this.getItemsByRfqId(response.id);
              let conversionFactor :number = 1;
              if (
                response.alternateSkus.length &&
                response.alternateSkus[0].unitConversionRule
              ) {
                conversionFactor =
                  +response.alternateSkus[0].unitConversionRule.conversionFactor;
              }
              const totalUnverifiedQuantity = this.getTotalUnverifiedQuantity(
                items,
                response.id,
              );
              if (this.scan.quantity > totalUnverifiedQuantity) {
                this.promptErrorUnverifiedQuantiy(
                  totalUnverifiedQuantity,
                  response.id,
                  response.internalSku,
                );
              } else {
                this.updateItems(response.id, items, conversionFactor);
              }
            }
            this.resetScanForm();
          },
          error: (response) => {
            this.promptErrorIncorrectProduct();
            this.resetScanForm();
          },
        });
    }
  }

  onVerify(indexItem :number, indexKitItem ?:number) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Are you sure you want to manual verify this order?',
          message: 'This will fulfill the scanned quantity',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          const item = this.currentPacking.items[indexItem];

          if (item.type === this.eItemType.PRODUCT) {
            item.scanQuantity = item.baseUomQuantity;
          } else {
            if (indexKitItem !== undefined) {
              item.kitItems[indexKitItem].scanQuantity =
                item.kitItems[indexKitItem].baseUomQuantity * item.baseUomQuantity;

              const totalUnverifiedKitComponentQuantity =
                this.getTotalUnverifiedKitComponentQuantity(item.kitItems, item);

              if (totalUnverifiedKitComponentQuantity === 0) {
                item.scanQuantity = item.baseUomQuantity;
              }
            }
          }
        }
      });
  }

  onClear(indexItem :number, indexKitItem ?:number) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title:
            'Are you sure you want to reset the verification process of this order?',
          message: 'This will reset the scanned quantity',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          const item = this.currentPacking.items[indexItem];

          if (item.type === this.eItemType.PRODUCT) {
            item.scanQuantity = 0;
          } else {
            if (indexKitItem !== undefined) {
              item.kitItems[indexKitItem].scanQuantity = 0;

              const totalUnverifiedKitComponentQuantity =
                this.getTotalUnverifiedKitComponentQuantity(item.kitItems, item);

              if (totalUnverifiedKitComponentQuantity > 0) {
                item.scanQuantity = 0;
              }
            }
          }
        }
      });
  }

  updateItems(
    rfqId :number,
    items :PicklistPackingItem[],
    conversionFactor :number = 1,
  ) :void {
    let tempScanQuantity = this.scan.quantity * conversionFactor;

    items.forEach((item :PicklistPackingItem, index :number) => {
      if (tempScanQuantity < 0) {
        return;
      }

      if (item.type === this.eItemType.PRODUCT) {
        if (!item.scanQuantity) {
          item.scanQuantity = 0;
        }

        if (item.scanQuantity === item.baseUomQuantity) {
          return;
        }

        let tempItemUnverifiedQuantity = item.baseUomQuantity - item.scanQuantity;

        if (item.baseUomQuantity === 0) {
          tempItemUnverifiedQuantity = 0;
        }

        tempScanQuantity = tempScanQuantity - tempItemUnverifiedQuantity;

        if (tempScanQuantity < 0) {
          item.scanQuantity = item.baseUomQuantity + tempScanQuantity;
        } else {
          item.scanQuantity = item.baseUomQuantity;
        }
      } else {
        const kitItems = this.getKitItemsByRfqId(rfqId, item);

        kitItems.forEach((kitItem :PicklistPackingKitItem, index :number) => {
          if (tempScanQuantity < 0) {
            return;
          }

          if (!kitItem.scanQuantity) {
            kitItem.scanQuantity = 0;
          }

          if (
            kitItem.scanQuantity ===
            kitItem.baseUomQuantity * item.baseUomQuantity
          ) {
            return;
          }

          let tempItemUnverifiedQuantity =
            kitItem.baseUomQuantity * item.baseUomQuantity - kitItem.scanQuantity;

          if (kitItem.baseUomQuantity === 0) {
            tempItemUnverifiedQuantity = 0;
          }

          tempScanQuantity = tempScanQuantity - tempItemUnverifiedQuantity;

          if (tempScanQuantity < 0) {
            kitItem.scanQuantity =
              kitItem.baseUomQuantity * item.baseUomQuantity + tempScanQuantity;
          } else {
            kitItem.scanQuantity = kitItem.baseUomQuantity * item.baseUomQuantity;
          }
        });

        const totalUnverifiedKitComponentQuantity =
          this.getTotalUnverifiedKitComponentQuantity(item.kitItems, item);

        if (totalUnverifiedKitComponentQuantity === 0) {
          item.scanQuantity = item.baseUomQuantity;
        }

        const kitScanNumbers = item.kitItems.reduce(
          (acc :Array<number>, kitItem :PicklistPackingKitItem) => {
            const next = kitItem.scanQuantity
              ? kitItem.scanQuantity / kitItem.baseUomQuantity
              : 0;

            acc.push(Math.trunc(next));

            return acc;
          },
          [],
        );

        const kitScanQuantity = kitScanNumbers.sort((a, b) => a - b);

        item.scanQuantity = kitScanQuantity[0];
      }
    });
  }

  promptErrorIncorrectProduct() :void {
    const dialogExist = this.dialog.getDialogById(
      'scanned-incorrect-product-warning',
    );

    if (!dialogExist) {
      this.dialog
        .open(UiWarningDialogComponent, {
          id: 'scanned-incorrect-product-warning',
          data: {
            title: 'Incorrect Product',
            message: 'The scanned item can not be found on this order',
          },
          width: DialogConfig.sizes.small,
        })
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$));
    }
  }

  promptErrorUnverifiedQuantiy(
    totalUnverifiedQuantity :number,
    rfqId :number,
    sku :string,
  ) :void {
    let affectedLineNumbers = '';
    let title = '';
    let message = '';

    if (totalUnverifiedQuantity === 0) {
      affectedLineNumbers = this.getAffectedLineNumbersFulfilled(rfqId);
      title = sku + ' already fulfilled';
      message = 'Affected Row #: ' + affectedLineNumbers;
    } else {
      affectedLineNumbers = this.getAffectedLineNumbers(rfqId);
      title = 'The inputted scan quantity exceeds';
      message =
        sku +
        ' remaining unverified: ' +
        totalUnverifiedQuantity +
        '<br/>Affected Row #: ' +
        affectedLineNumbers;
    }

    const dialogExist = this.dialog.getDialogById('scanned-quantity-warning');

    if (!dialogExist) {
      this.dialog
        .open(UiWarningDialogComponent, {
          id: 'scanned-quantity-warning',
          data: {
            title: title,
            message: message,
          },
          width: DialogConfig.sizes.small,
        })
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$));
    }
  }

  getItemsByRfqId(rfqId :number) :PicklistPackingItem[] {
    return this.currentPacking.items.reduce((acc :PicklistPackingItem[], item) => {
      const next = item.kitItems.filter(function (kitItem :PicklistPackingKitItem) {
        return kitItem.rfqId === rfqId;
      });

      if (item.rfqId === rfqId || next.length > 0) {
        acc.push(item);
      }

      return acc;
    }, []);
  }

  getKitItemsByRfqId(
    rfqId :number,
    item :PicklistPackingItem,
  ) :PicklistPackingKitItem[] {
    return item.kitItems.filter(function (kitItem :PicklistPackingKitItem) {
      return kitItem.rfqId === rfqId;
    });
  }

  resetScanForm() :void {
    this.scan.sku = null;
    this.scan.quantity = 1;
    this.shouldBeep = true;
    this.isCallbackTriggered = false;
  }

  onAddQuantity() :void {
    this.dialog
      .open(PicklistVirtualCalculatorFormComponent, {
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((quantity :number) => {
        if (quantity) {
          this.scan.quantity = quantity;
        } else {
          this.scan.quantity = 1;
        }

        this.searchInputRef?.nativeElement?.focus();
      });
  }

  private getDataFromService() :void {
    this.picklistService.loadingScan$.subscribe((loadingState :boolean) => {
      this.loadingScan = loadingState;
    });
  }

  private getAffectedLineNumbersFulfilled(rfqId :number) :string {
    const items = this.currentPacking.items.reduce(
      (acc :PicklistPackingItem[], item :PicklistPackingItem, index :number) => {
        const next = item.kitItems.filter(function (
          kitItem :PicklistPackingKitItem,
        ) {
          return (
            kitItem.rfqId === rfqId &&
            kitItem.scanQuantity === kitItem.baseUomQuantity * item.baseUomQuantity
          );
        });

        if (
          (item.rfqId === rfqId && item.scanQuantity === item.baseUomQuantity) ||
          next.length > 0
        ) {
          acc[index] = item;
        }

        return acc;
      },
      [],
    );

    return items.reduce(
      (acc :string, item :PicklistPackingItem, lineNumber :number) => {
        if (lineNumber === 0) {
          return (lineNumber + 1).toString();
        } else {
          const comma = acc === '' ? '' : ', ';

          return acc + comma + (lineNumber + 1).toString();
        }
      },
      '',
    );
  }

  private getAffectedLineNumbers(rfqId :number) :string {
    const items = this.currentPacking.items.reduce(
      (acc :PicklistPackingItem[], item :PicklistPackingItem, index :number) => {
        const next = item.kitItems.filter(function (
          kitItem :PicklistPackingKitItem,
        ) {
          return (
            kitItem.rfqId === rfqId &&
            kitItem.scanQuantity !== kitItem.baseUomQuantity * item.baseUomQuantity
          );
        });

        if (
          (item.rfqId === rfqId && item.scanQuantity !== item.baseUomQuantity) ||
          next.length > 0
        ) {
          acc[index] = item;
        }

        return acc;
      },
      [],
    );

    return items.reduce(
      (acc :string, item :PicklistPackingItem, lineNumber :number) => {
        if (lineNumber === 0) {
          return (lineNumber + 1).toString();
        } else {
          const comma = acc === '' ? '' : ', ';

          return acc + comma + (lineNumber + 1).toString();
        }
      },
      '',
    );
  }

  private getTotalUnverifiedQuantity(
    items :PicklistPackingItem[],
    rfqId :number,
  ) :number {
    return items.reduce((acc :number, item :PicklistPackingItem) => {
      if (item.type === this.eItemType.KIT) {
        const kitItems = item.kitItems.filter(function (kitItem) {
          return kitItem.rfqId === rfqId;
        });

        const totalUnverifiedKitComponentQuantity =
          this.getTotalUnverifiedKitComponentQuantity(kitItems, item);

        return acc + totalUnverifiedKitComponentQuantity;
      } else {
        return (
          acc + (item.baseUomQuantity - (item.scanQuantity ? item.scanQuantity : 0))
        );
      }
    }, 0);
  }

  private getTotalUnverifiedKitComponentQuantity(
    kitItems :PicklistPackingKitItem[],
    item :PicklistPackingItem,
  ) :number {
    return kitItems.reduce((acc :number, kitItem :PicklistPackingKitItem) => {
      return (
        acc +
        (kitItem.baseUomQuantity * item.baseUomQuantity -
          (kitItem.scanQuantity ? kitItem.scanQuantity : 0))
      );

      return acc;
    }, 0);
  }

  get orderQty() :number {
    return this.currentPacking?.items?.reduce(
      (acc :number, item :PicklistPackingItem) => acc + item.baseUomQuantity,
      0,
    );
  }

  get scanQuantity() :number {
    return this.currentPacking?.items?.reduce(
      (acc :number, item :PicklistPackingItem) => {
        if (item.scanQuantity) {
          return acc + item.scanQuantity;
        }

        return acc;
      },
      0,
    );
  }
}
